// The espionage and captivity intents: sending a spy, and what a captor
// does with a royal. Each answers whether the sim took it.

import type { createRustSimAdapter } from '@aod/sim-adapter';
import { type RefObject, useMemo } from 'react';
import type { AgentChoice, CaptureChoice } from './applyIntent.js';

/** What a sabotage order may add: a building to hit, and a house to blame (`espionage.false_flag`). */
export interface SabotageOptions {
  readonly buildingId?: string;
  readonly frameSeat?: number;
}

/** The hostile orders, as the right panel takes them. */
export type OpsActions = Pick<
  EspionageActions,
  | 'sabotageCity'
  | 'poisonSupply'
  | 'assassinate'
  | 'postCounterSpy'
  | 'recallCounterSpy'
  | 'sweep'
  | 'intercept'
  | 'decideAgent'
>;

/** The counter-intelligence orders (module `espionage.counter_intel`). */
export type CounterActions = Pick<
  EspionageActions,
  'postCounterSpy' | 'recallCounterSpy' | 'sweep' | 'intercept'
>;

export interface EspionageActions {
  /** Send a spy to watch an enemy city. */
  readonly dispatchSpy: (unitId: string, targetCityId: string) => boolean;
  /** Order a spy or assassin to wreck a building in an enemy city (module `espionage.ops`). */
  readonly sabotageCity: (unitId: string, targetCityId: string, opts?: SabotageOptions) => boolean;
  /** Send an assassin against a royal at their court (module `espionage.ops`). */
  readonly assassinate: (unitId: string, royalId: number, frameSeat?: number) => boolean;
  /** Order a spy to poison a well or granary in an enemy city (module `espionage.ops`). */
  readonly poisonSupply: (
    unitId: string,
    targetCityId: string,
    supply: 'well' | 'granary',
    frameSeat?: number,
  ) => boolean;
  /** Station a spy at one of your cities (module `espionage.counter_intel`). */
  readonly postCounterSpy: (unitId: string, cityId: string) => boolean;
  /** Take a counter-spy off its post. */
  readonly recallCounterSpy: (unitId: string) => boolean;
  /** Reveal the hidden foreign agents near a detector. */
  readonly sweep: (unitId: string) => boolean;
  /** Try to catch a detected foreign agent beside you. */
  readonly intercept: (unitId: string, targetUnitId: string) => boolean;
  /** Settle an agent you caught. */
  readonly decideAgent: (captiveId: number, choice: AgentChoice) => boolean;
  /** Decide what happens to a royal you hold. */
  readonly chooseCapture: (royalId: number, choice: CaptureChoice) => boolean;
  /** Send a spy to walk a royal out of where they are kept. */
  readonly spyRescue: (unitId: string, royalId: number) => boolean;
  /** Take the place a royal is kept by force. */
  readonly raidRescue: (royalId: number) => boolean;
  /** Pay for a royal you would rather have back. */
  readonly payRansom: (royalId: number) => boolean;
}

/** What a sim without the module (or before it boots) offers: nothing happens. */
export const NO_ESPIONAGE: EspionageActions = {
  dispatchSpy: () => false,
  sabotageCity: () => false,
  assassinate: () => false,
  postCounterSpy: () => false,
  recallCounterSpy: () => false,
  sweep: () => false,
  intercept: () => false,
  decideAgent: () => false,
  poisonSupply: () => false,
  chooseCapture: () => false,
  spyRescue: () => false,
  raidRescue: () => false,
  payRansom: () => false,
};

export function useEspionageIntents(
  adapterRef: RefObject<ReturnType<typeof createRustSimAdapter> | null>,
  localPlayerIndex: number,
): EspionageActions {
  return useMemo(() => {
    const send = (intent: Record<string, unknown>): boolean => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      return adapter.intent({ ...intent, playerIndex: localPlayerIndex } as never).ok;
    };
    return {
      dispatchSpy: (unitId, targetCityId) => send({ kind: 'dispatch_spy', unitId, targetCityId }),
      sabotageCity: (unitId, targetCityId, opts) =>
        send({ kind: 'sabotage_city', unitId, targetCityId, ...opts }),
      assassinate: (unitId, royalId, frameSeat) =>
        send({ kind: 'assassinate', unitId, royalId, frameSeat }),
      poisonSupply: (unitId, targetCityId, supply, frameSeat) =>
        send({ kind: 'poison_supply', unitId, targetCityId, supply, frameSeat }),
      postCounterSpy: (unitId, cityId) => send({ kind: 'post_counter_spy', unitId, cityId }),
      recallCounterSpy: (unitId) => send({ kind: 'recall_counter_spy', unitId }),
      sweep: (unitId) => send({ kind: 'sweep', unitId }),
      intercept: (unitId, targetUnitId) => send({ kind: 'intercept', unitId, targetUnitId }),
      decideAgent: (captiveId, choice) => send({ kind: 'decide_agent', captiveId, choice }),
      chooseCapture: (royalId, choice) => send({ kind: 'choose_capture', royalId, choice }),
      spyRescue: (unitId, royalId) => send({ kind: 'spy_rescue', unitId, royalId }),
      raidRescue: (royalId) => send({ kind: 'raid_rescue', royalId }),
      payRansom: (royalId) => send({ kind: 'pay_ransom', royalId }),
    };
  }, [adapterRef, localPlayerIndex]);
}
